import { useMutation, useQuery } from '@tanstack/react-query'
import { useId, useState, type FormEvent, type ReactNode } from 'react'
import { useSearchParams } from 'react-router'

import {
  coordinationAvailabilityKey,
  emergencyHelpHospitalsKey,
  fetchCoordinationAvailability,
  fetchEmergencyHelpHospitals,
  sendCoordinationRequest,
  type CoordinationRequestBody,
} from '../../api/emergencyHelp'
import { Icon } from '../../components/Icon'
import { ApiError } from '../../lib/api'
import { amtcDialNumber, amtcDisplayNumber } from '../../lib/amtc'
import { checkGhanaPhone } from '../../lib/ghanaPhone'
import {
  BackLink,
  Call112Button,
  EmergencyHelpShell,
  Inline112,
} from './chrome'
import { useArrivalFocus } from './focus'
import { hospitalPath, hospitalsPath } from './paths'

/**
 * Emergency Help, screen 4: ask AMTC to coordinate with a hospital (M36-T08,
 * spec §C.1.4, ADR-0065).
 *
 * - It exists only while a coordinator is on duty. Reached with the switch off
 *   (a stale link), it says so, shows 112, and has no form.
 * - 112 stays first: the header's pill, before anything this screen loads.
 * - It never claims more than "request received". Never "help is on the way",
 *   "a coordinator is calling you" or "hospital accepted".
 * - The person's location reaches the server ONLY if they press "Use my
 *   location" on this screen and then send. Screens 1–3 never send it.
 * - Every failure offers 112 and the AMTC line as tappable numbers. Never a
 *   blank error.
 */

const DESCRIPTION_MAX = 500
const LOCATION_MAX = 300

/** Ghana with a margin: the server's bounds (`GHANA_LAT`/`GHANA_LNG` in
 * backend/app/schemas/hospital_emergency_info.py). A position outside them
 * would be refused, every time, so it is never sent. */
const GHANA_LAT = [4.5, 11.3] as const
const GHANA_LNG = [-3.4, 1.3] as const
const inGhana = (lat: number, lng: number) =>
  lat >= GHANA_LAT[0] &&
  lat <= GHANA_LAT[1] &&
  lng >= GHANA_LNG[0] &&
  lng <= GHANA_LNG[1]

/** A hospital slug the server would accept (`hospital_slug`, ≤100). Anything
 * else from the address bar is dropped rather than sent and refused. */
const SLUG = /^[a-z0-9-]{1,100}$/

const INPUT =
  'w-full rounded-r3 border border-line-2 bg-bg-app px-3 py-2.5 text-[14px] text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

export function EmergencyRequest(): ReactNode {
  const [params] = useSearchParams()
  const available = useQuery({
    queryKey: coordinationAvailabilityKey,
    queryFn: ({ signal }) => fetchCoordinationAvailability(signal),
    retry: false,
    networkMode: 'always',
    staleTime: 0,
  })
  const [reference, setReference] = useState<string | null>(null)
  const heading = useArrivalFocus<HTMLHeadingElement>(!available.isPending)
  const from = params.get('hospital') ?? ''

  return (
    <EmergencyHelpShell step={4} title="Request AMTC help">
      <BackLink to={from ? hospitalPath(from) : hospitalsPath}>
        Back to the hospital
      </BackLink>
      {reference ? (
        <Received reference={reference} />
      ) : available.isPending ? (
        <>
          <p role="status" className="text-[13px] text-ink-3">
            Checking whether AMTC coordination is available…
          </p>
          <Call112Button />
        </>
      ) : available.data === true ? (
        <RequestForm
          initialHospital={from}
          headingRef={heading}
          onSent={setReference}
        />
      ) : (
        <div className="flex flex-col gap-3">
          <h1
            ref={heading}
            tabIndex={-1}
            className="font-display text-d-xs font-medium tracking-display text-ink-1 focus:outline-none"
          >
            AMTC coordination is not available right now
          </h1>
          <p className="text-[13px] text-ink-2">
            In an emergency, do not wait. Call emergency services.
          </p>
          <Call112Button />
        </div>
      )}
    </EmergencyHelpShell>
  )
}

function AmtcLink(): ReactNode {
  const dial = amtcDialNumber()
  if (!dial) return null
  return (
    <>
      , or call AMTC on{' '}
      <a
        href={`tel:${dial}`}
        className="font-semibold text-ink-1 underline underline-offset-2"
      >
        {amtcDisplayNumber() ?? dial}
      </a>
    </>
  )
}

/** The failure line for a refused or failed send. Each offers 112 (and AMTC). */
function failureFor(error: unknown): ReactNode {
  if (error instanceof ApiError && error.status === 409) {
    return (
      <>
        Coordination is not available right now — call <Inline112 />
        <AmtcLink />.
      </>
    )
  }
  if (error instanceof ApiError && error.status === 429) {
    return (
      <>
        Too many requests from this device. Call <Inline112 />
        <AmtcLink />.
      </>
    )
  }
  return (
    <>
      We couldn&rsquo;t send your request. Call <Inline112 />
      <AmtcLink />.
    </>
  )
}

function RequestForm({
  initialHospital,
  headingRef,
  onSent,
}: {
  initialHospital: string
  headingRef: React.Ref<HTMLHeadingElement>
  onSent: (reference: string) => void
}): ReactNode {
  const id = useId()
  const hospitals = useQuery({
    queryKey: emergencyHelpHospitalsKey,
    queryFn: ({ signal }) => fetchEmergencyHelpHospitals(signal),
    retry: 1,
    networkMode: 'always',
  })
  const [phone, setPhone] = useState('')
  const [phoneTouched, setPhoneTouched] = useState(false)
  const [locationText, setLocationText] = useState('')
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    null,
  )
  const [locating, setLocating] = useState<
    'idle' | 'busy' | 'failed' | 'outside'
  >('idle')
  const [description, setDescription] = useState('')
  const [hospital, setHospital] = useState(
    SLUG.test(initialHospital) ? initialHospital : '',
  )
  const [consent, setConsent] = useState(false)
  const [feedback, setFeedback] = useState(false)

  const phoneCheck = checkGhanaPhone(phone)
  const ready = phoneCheck.ok && consent

  const send = useMutation({
    mutationFn: (body: CoordinationRequestBody) =>
      sendCoordinationRequest(body),
    onSuccess: onSent,
  })

  const useMyLocation = () => {
    if (!('geolocation' in navigator)) {
      setLocating('failed')
      return
    }
    setLocating('busy')
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const { latitude: lat, longitude: lng } = p.coords
        if (!inGhana(lat, lng)) {
          // Never sent: the server would refuse it, and every retry with it.
          setCoords(null)
          setLocating('outside')
          return
        }
        setCoords({ lat, lng })
        setLocating('idle')
      },
      () => setLocating('failed'),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 },
    )
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!phoneCheck.ok || !consent) return
    const body: CoordinationRequestBody = {
      callback_phone: phoneCheck.normalised,
      consent: true,
    }
    if (locationText.trim()) body.location_text = locationText.trim()
    if (coords) {
      body.lat = coords.lat
      body.lng = coords.lng
    }
    if (description.trim()) body.description = description.trim()
    if (hospital && SLUG.test(hospital)) body.hospital_slug = hospital
    if (feedback) body.feedback_consent = true
    send.mutate(body)
  }

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      <div>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="font-display text-d-xs font-medium tracking-display text-ink-1 focus:outline-none"
        >
          Request AMTC help
        </h1>
        <p className="mt-1 text-[13px] text-ink-2">
          Send your details to AMTC. A coordinator may phone you back on the
          number you give. If anyone is in danger, call <Inline112 /> first.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={`${id}-phone`}
          className="text-[13px] font-semibold text-ink-1"
        >
          Your phone number (for the call back)
        </label>
        <input
          id={`${id}-phone`}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onBlur={() => setPhoneTouched(true)}
          aria-invalid={phoneTouched && !phoneCheck.ok}
          aria-describedby={`${id}-phone-help`}
          className={INPUT}
        />
        <p id={`${id}-phone-help`} className="text-[12px] text-ink-3">
          {phoneTouched && !phoneCheck.ok
            ? phoneCheck.message
            : 'A Ghana number: 0 and 9 digits, or +233 and 9 digits.'}
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={`${id}-where`}
          className="text-[13px] font-semibold text-ink-1"
        >
          Where are you?{' '}
          <span className="font-normal text-ink-3">(optional)</span>
        </label>
        <input
          id={`${id}-where`}
          value={locationText}
          maxLength={LOCATION_MAX}
          onChange={(e) => setLocationText(e.target.value)}
          placeholder="e.g. Madina market, near the lorry station"
          className={INPUT}
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={useMyLocation}
            disabled={locating === 'busy'}
            className="inline-flex items-center gap-1.5 rounded-r3 border border-line-2 px-3 py-2 text-[13px] font-medium text-ink-1 hover:bg-bg-soft disabled:opacity-60"
          >
            <Icon name="pin" size={14} /> Use my location
          </button>
          {coords ? (
            <button
              type="button"
              onClick={() => setCoords(null)}
              className="text-[13px] font-medium text-ink-2 underline underline-offset-2"
            >
              Don&rsquo;t send my location
            </button>
          ) : null}
          <span className="text-[12px] text-ink-3" aria-live="polite">
            {coords
              ? 'Your location will be sent with this request.'
              : locating === 'busy'
                ? 'Finding your location…'
                : locating === 'failed'
                  ? 'Could not get your location. Describe where you are instead.'
                  : locating === 'outside'
                    ? 'Your location seems to be outside Ghana, so it will not be sent. Describe where you are instead.'
                    : ''}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={`${id}-what`}
          className="text-[13px] font-semibold text-ink-1"
        >
          What help do you need?{' '}
          <span className="font-normal text-ink-3">(optional)</span>
        </label>
        <textarea
          id={`${id}-what`}
          rows={3}
          value={description}
          maxLength={DESCRIPTION_MAX}
          onChange={(e) => setDescription(e.target.value)}
          aria-describedby={`${id}-what-help`}
          className={INPUT}
        />
        <p id={`${id}-what-help`} className="text-[12px] text-ink-3">
          Don&rsquo;t include medical history. {description.length}/
          {DESCRIPTION_MAX}
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={`${id}-hospital`}
          className="text-[13px] font-semibold text-ink-1"
        >
          Hospital
        </label>
        <select
          id={`${id}-hospital`}
          value={hospital}
          onChange={(e) => setHospital(e.target.value)}
          className={INPUT}
        >
          <option value="">No preference</option>
          {(hospitals.data?.data ?? []).map((h) => (
            <option key={h.slug} value={h.slug}>
              {h.name}
            </option>
          ))}
          {/* Carried from screen 3 even before the list has loaded. */}
          {hospital &&
          !hospitals.data?.data.some((h) => h.slug === hospital) ? (
            <option value={hospital}>{hospital}</option>
          ) : null}
        </select>
      </div>

      <label className="flex items-start gap-2.5 text-[13px] text-ink-1">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4"
          required
        />
        <span>
          I consent to sharing these details with AMTC for this request.
        </span>
      </label>
      <label className="flex items-start gap-2.5 text-[13px] text-ink-1">
        <input
          type="checkbox"
          checked={feedback}
          onChange={(e) => setFeedback(e.target.checked)}
          className="mt-0.5 h-4 w-4"
        />
        <span>
          AMTC may contact me later to ask about the help I received.{' '}
          <span className="text-ink-3">(optional)</span>
        </span>
      </label>

      <button
        type="submit"
        disabled={!ready || send.isPending}
        aria-describedby={ready ? undefined : `${id}-send-help`}
        className="flex items-center justify-center rounded-r3 bg-ink-1 px-4 py-3 text-[15px] font-semibold text-bg-app disabled:opacity-50"
      >
        {send.isPending ? 'Sending…' : 'Send assistance request'}
      </button>
      {ready ? null : (
        // Why the button is off, said out loud: a disabled button gives no
        // reason of its own (review of M36-T08).
        <p id={`${id}-send-help`} className="-mt-2 text-[12px] text-ink-3">
          {!phoneCheck.ok && !consent
            ? 'Enter a Ghana phone number and tick the consent box to send.'
            : !phoneCheck.ok
              ? 'Enter a Ghana phone number to send.'
              : 'Tick the consent box to send.'}
        </p>
      )}

      {send.isError ? (
        <p
          role="alert"
          className="rounded-r3 border border-sos/40 bg-sos-bg px-3 py-2.5 text-[13px] font-medium text-ink-1"
        >
          {failureFor(send.error)}
        </p>
      ) : null}
    </form>
  )
}

/** The confirmation. Never more than "received" (spec §C.1.4). */
function Received({ reference }: { reference: string }): ReactNode {
  const heading = useArrivalFocus<HTMLHeadingElement>(true)
  return (
    <section
      role="status"
      aria-labelledby="eh-received"
      className="flex flex-col gap-2 rounded-r3 border border-bed-warn/40 bg-bed-warn-bg px-4 py-4"
    >
      <h1
        id="eh-received"
        ref={heading}
        tabIndex={-1}
        className="text-[16px] font-semibold text-ink-1 focus:outline-none"
      >
        Request received — awaiting staff acknowledgement.
      </h1>
      <p className="text-[13px] text-ink-1">
        Your reference:{' '}
        <span className="font-mono font-semibold">{reference}</span>
      </p>
      <p className="text-[13px] text-ink-1">
        For immediate medical help, call <Inline112 />.
      </p>
    </section>
  )
}
